import tierProxy from 'Map/tier/tierProxy';
import tierStyleRetriever from 'Map/map/tierStyleRetriever';
import setTierInStyleOnPosition from 'Map/tier/styleUpdate/addTier';
import tierInStylePositionDesignator from 'Map/tier/styleUpdate/tierInStylePositionDesignator';
import removeTierFromStyle from 'Map/tier/styleUpdate/removeTier';
import modifyTileCacheForCertainTiers from 'Map/map/tileCacheModifier';
import converters from 'Services/services/converters';
import {TIER_TYPE} from 'Map/tier/mapTier';
import updateLayersVisibility from 'Map/tier/styleUpdate/updateLayersVisibility';
import mapTiersFactory from 'Map/configurer/mapTiersFactory';

/**
 * @class Map
 * @namespace Maps
 */

class ExtendedMap {
    constructor(tiersContainer, options = {}) {
        this.options = options;
        this._tiersContainer = tiersContainer;
        this._tierStyleRetriever = tierStyleRetriever();
        this._language = options.language;
        this._geopoliticalView = options.geopoliticalView;
    }

    _populateTiers = async () => {
        const tiers = mapTiersFactory(this.options);
        for (let i = 0; i < tiers.length; i++) {
            await this._addTier(tiers[i], i === 0); //eslint-disable-line
        }
    }

    _downloadTierStyleIfNeeded = async (tier) => {
        await this._tierStyleRetriever.fetch(tier);
    };

    _addTierToTheContainer = (tier) => {
        this._tiersContainer.addTier(tier);
    };

    _setTierStyleAsMapStyle = (tier) => {
        const style = JSON.parse(JSON.stringify(tier.getStyle()));
        if (this.options.sprite) {
            style.sprite = this.options.sprite;
        }
        if (this.options.glyphs) {
            style.glyphs = this.options.glyphs;
        }
        const promise = new Promise((resolve) => {
            this.__om.once('style.load', () => {
                resolve();
            });
        });
        this.__om.setStyle(style, {diff: false});
        return promise;
    };

    _insertTierIntoProperPositionInStyle = async (tier) => {
        const positionDesignator = tierInStylePositionDesignator(this, this._tiersContainer.getTiers());
        const beforeLayerId = positionDesignator.findBeforeLayerIdFor(tier);
        setTierInStyleOnPosition(this, tier, beforeLayerId);
    };

    _addTierToMapStyle = async (tier) => {
        if (this._tiersContainer.length() === 0) {
            this._addTierToTheContainer(tier);
            return this._setTierStyleAsMapStyle(tier);
        } else {
            this._addTierToTheContainer(tier);
            return this._insertTierIntoProperPositionInStyle(tier);
        }
    };

    hasTier = (tierId) => {
        return this._tiersContainer.hasTier(tierId);
    };

    _getBaseTier = () => {
        return this._getTier(TIER_TYPE.BASE);
    };

    _getSymbolsTier = () => {
        return this._getTier(TIER_TYPE.LABELS);
    };

    _getBuildingsTier = () => {
        return this._getTier(TIER_TYPE.BUILDINGS);
    };

    _attachTier = async (tier) => {
        await tier.setLanguage(this._language);
        await tier.setGeopoliticalView(this._geopoliticalView);
        await tier.onBeforeAdd(this);
        await this._downloadTierStyleIfNeeded(tier);
        await this._addTierToMapStyle(tier);
        await tier.onAdd(this);
        this._enableSymbolsTierIfNecessary(tier);
        modifyTileCacheForCertainTiers(this);
        return true;
    };

    _updateTiers = async (tier, updateMethod, instantly) => {
        if (this.loaded() || instantly) {
            return updateMethod(tier);
        } else {
            const that = this;
            return new Promise((resolve) => {
                that.once('render', () => {
                    resolve(updateMethod(tier));
                });
            });
        }
    };

    _removeTierFromMapStyle = (tier) => {
        const shouldTierSourceBeRemoved = true; //for now, but we need to do it more smartly later
        removeTierFromStyle(this, tier, shouldTierSourceBeRemoved);
    };

    _getTier = (tierId) => {
        return this._tiersContainer.getTier(tierId);
    };

    _removeTierFromContainer = async (tierId) => {
        const tier = this._tiersContainer.removeTier(tierId);
        await tier.onRemove(this);
    };

    _enableSymbolsTierIfNecessary = (tier) => this._updateSymbolsTierIfNecessary(tier, true);

    _disableSymbolsTierIfNecessary = (tier) => this._updateSymbolsTierIfNecessary(tier, false);

    _updateSymbolsTierIfNecessary = (tier, visibility) => {
        const baseTier = this._getBaseTier();
        if (!baseTier || !baseTier.isRaster()) {
            return;
        }

        const symbolsTier = this._getSymbolsTier();

        if (!symbolsTier) {
            return;
        }

        const position = tier.getPosition();
        const baseAndSymbolsLayerCount = 2;
        const existenceCondition = visibility ? this._tiersContainer.length() > baseAndSymbolsLayerCount :
            this._tiersContainer.length() <= baseAndSymbolsLayerCount;
        if (baseTier.getPosition() < position && position < symbolsTier.getPosition() && existenceCondition) {
            symbolsTier.setVisible(visibility);
            updateLayersVisibility(this, symbolsTier.getStyle().layers, visibility ? 'visible' : 'none');
        }
    };

    _detachTier = async (tier) => {
        await this._removeTierFromContainer(tier.getId());
        this._removeTierFromMapStyle(tier);
        this._disableSymbolsTierIfNecessary(tier);
        return true;
    };

    _addTier = async (tier, instantly) => {
        const proxiedTier = tierProxy(tier);

        if (this.hasTier(proxiedTier.getId())) {
            return false;
        }

        return this._updateTiers(proxiedTier, this._attachTier, instantly);
    };

    /**
     * Adds a tier.
     *
     * @method addTier
     * @param {Object} tier Use this method to add a TrafficIncidentTier or TrafficFlowTilesTier instance
     * to the map.
     * @return {Promise<boolean>} Returns 'false' if the tier was already added, 'true' otherwise.
     */
    addTier = async (tier) => {
        return this._addTier(tier, false);
    };

    /**
     * Removes a tier.
     *
     * @method removeTier
     * @param {String} tierId The tier id.
     * @return {Promise<boolean>} Returns 'false' if the tier was not in the list of tiers, 'true' otherwise.
     */
    removeTier = async (tierId) => {
        if (!this.hasTier(tierId)) {
            return false;
        }
        const tier = this._getTier(tierId);
        return this._updateTiers(tier, this._detachTier);
    };

    /**
     * This method sets a language for a given map. This setting will affect only map tiles and not other
     * services, e.g., search. Providing an empty string ('') as an input value will unset this option. Supported
     * languages can be obtained from the list of:
     * <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-vector/tile#listOfSupportedLanguages">
     *     Supported Languages</a> page.
     *
     * @method setLanguage
     * @param {String} language
     * @return {Promise}
     */
    setLanguage = async (language) => {
        language = converters.language(language, 'maps');
        this._language = language;
        return Promise.all(this._tiersContainer.getTiers().map((tier) => tier.setLanguage(language)))
            .then((results) => {
                return results.some((result) => result);
            });
    };

    /**
     * This method returns a language set.
     *
     * @method getLanguage
     * @return {String}
     */
    getLanguage = () => {
        return this._language;
    };

    /**
     * This method sets a geopolitical view for a given map. This setting will only affect map tiles and not
     * other services, e.g., search. Providing an empty string ('') as an input value will unset this option. Supported
     * geopolitical views can be obtained from
     * <a href="https://developer.tomtom.com/maps-api/maps-api-documentation-vector/tile#request-data">
     *     here</a>.
     *
     * @method setGeopoliticalView
     * @param {String} geopoliticalView
     * @return {Promise}
     */
    setGeopoliticalView = (geopoliticalView) => {
        geopoliticalView = converters.geopoliticalView(geopoliticalView);
        this._geopoliticalView = geopoliticalView;
        return Promise.all(this._tiersContainer.getTiers().map((tier) => tier.setGeopoliticalView(geopoliticalView)))
            .then((results) => {
                return results.some((result) => result);
            });
    };

    /**
     * This method returns a geopolitical view set.
     *
     * @method getGeopoliticalView
     * @return {String}
     */
    getGeopoliticalView = () => {
        return this._geopoliticalView;
    };

    /**
     * Returns a TomTomAttributionControl instance.
     *
     * @method getAttributionControl
     * @return {Maps.TomTomAttributionControl}
     */
    getAttributionControl = () => {
        return this.__om._attributionControl || this._attributionControl;
    }
}

export default (tiersContainer, options) => {
    return new ExtendedMap(tiersContainer, options);
};



// WEBPACK FOOTER //
// ./map/map/extendedMap.js